import type { Adjacency, PlaneFit, RegionGrowOptions, RegionGrowResult, Vec3 } from './types';
import { dot, scale, vertexAt } from './vec';
import { triCentroid } from './triangle';
import { accumulateTriangle, createPlaneAccumulator, fitFromAccumulator } from './plane.fit';

/** Read a precomputed per-triangle normal from a (smoothed) normals buffer. */
const normalAt = (normals: Float32Array, t: number): Vec3 => [normals[t * 3], normals[t * 3 + 1], normals[t * 3 + 2]];

/** Max |signed distance to plane| over a triangle's 3 vertices. */
function maxVertexDist(positions: ArrayLike<number>, indices: ArrayLike<number>, t: number, plane: PlaneFit): number {
  let max = 0;
  for (let k = 0; k < 3; k++) {
    const v = vertexAt(positions, indices[t * 3 + k]);
    const d = Math.abs(
      (v[0] - plane.point[0]) * plane.normal[0] +
        (v[1] - plane.point[1]) * plane.normal[1] +
        (v[2] - plane.point[2]) * plane.normal[2],
    );
    if (d > max) max = d;
  }
  return max;
}

/** Seed plane from ≤3 BFS rings around the seed (cap 32 tris, pre-filtered to 25°). */
function collectSeedNeighborhood(adj: Adjacency, normals: Float32Array, seed: number, seedNormal: Vec3): number[] {
  const cos25 = Math.cos((25 * Math.PI) / 180);
  const out = [seed];
  const visited = new Set<number>([seed]);
  let frontier = [seed];
  for (let ring = 0; ring < 3 && out.length < 32; ring++) {
    const next: number[] = [];
    for (const t of frontier) {
      for (let s = 0; s < 3; s++) {
        const nb = adj.adjacent[t * 3 + s];
        if (nb >= 0 && !visited.has(nb)) {
          visited.add(nb);
          if (Math.abs(dot(normalAt(normals, nb), seedNormal)) >= cos25) {
            out.push(nb);
            next.push(nb);
            if (out.length >= 32) break;
          }
        }
      }
      if (out.length >= 32) break;
    }
    frontier = next;
  }
  return out;
}

/**
 * Grow a planar region from a seed triangle via edge-adjacency BFS.
 *
 * A frontier triangle is accepted iff:
 *   (a) angle(smoothedNormal, plane.normal) ≤ angleTolerance  (rejects spikes), AND
 *   (b) max over its 3 vertices of |distance to plane| ≤ max(absFloor, rel·regionDiag)
 *       (rejects creep onto curvature; stricter than a centroid test).
 * The plane is refit incrementally on a doubling schedule as the region grows.
 * BFS depth (geodesic-ish hop count) is returned per triangle to drive the pulse.
 *
 * All inputs are in WELDED index space (so growth crosses chunk seams). Positions,
 * normals, areas and adjacency must be consistent with `weldedIndices`.
 */
export function growPlanarRegion(
  positions: Float32Array,
  weldedIndices: Uint32Array,
  adj: Adjacency,
  smoothedNormals: Float32Array,
  areas: Float32Array,
  seedTriangle: number,
  bboxDiag: number,
  opts: RegionGrowOptions = {},
): RegionGrowResult {
  const angleTol = ((opts.angleToleranceDeg ?? 12) * Math.PI) / 180;
  const cosAngleTol = Math.cos(angleTol);
  const distanceRel = opts.distanceRel ?? 0.01;
  const distanceAbsFloor = (opts.distanceAbsFloor ?? 0.003) * bboxDiag;
  const maxTriangles = opts.maxTriangles ?? 250_000;
  const triCount = weldedIndices.length / 3;

  // An out-of-range seed would read NaN positions/normals — the NaN comparisons then
  // silently PASS the angle test and return a garbage one-triangle region. Refuse it.
  if (!Number.isInteger(seedTriangle) || seedTriangle < 0 || seedTriangle >= triCount) {
    return {
      triangles: new Uint32Array(0),
      depths: new Uint32Array(0),
      plane: { normal: [0, 1, 0], point: [0, 0, 0], rms: 0 },
      seedTriangle,
      maxDepth: 0,
      truncated: false,
    };
  }

  const seedNormal = normalAt(smoothedNormals, seedTriangle);

  // Seed plane.
  const seedTris = collectSeedNeighborhood(adj, smoothedNormals, seedTriangle, seedNormal);
  let plane: PlaneFit;
  if (seedTris.length < 3) {
    plane = { normal: seedNormal, point: triCentroid(positions, weldedIndices, seedTriangle), rms: 0 };
  } else {
    const seedAcc = createPlaneAccumulator();
    for (const t of seedTris) accumulateTriangle(seedAcc, positions, weldedIndices, t, areas[t]);
    plane = fitFromAccumulator(seedAcc);
    if (dot(plane.normal, seedNormal) < 0) plane = { ...plane, normal: scale(plane.normal, -1) };
  }

  const accepted: number[] = [];
  const depths: number[] = [];
  const visited = new Uint8Array(triCount);
  const acc = createPlaneAccumulator();
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  let regionDiag = 0;
  let nextRefit = 16;
  let maxDepth = 0;
  let truncated = false;

  const queue: number[] = [seedTriangle];
  const queueDepth: number[] = [0];
  visited[seedTriangle] = 1;
  let qi = 0;

  while (qi < queue.length) {
    const t = queue[qi];
    const d = queueDepth[qi];
    qi++;

    // The SEED is exempt from both tests: near a crease the ≤32-tri neighborhood fit
    // can tilt >angleTol away from the seed's own smoothed normal, and rejecting the
    // very triangle the user tapped turns a visibly flat spot into a silent no-op.
    // (The distance test was always seed-exempt via `accepted.length > 0`.)
    if (accepted.length > 0 && Math.abs(dot(normalAt(smoothedNormals, t), plane.normal)) < cosAngleTol) continue;

    const distTol = Math.max(distanceAbsFloor, distanceRel * Math.max(regionDiag, 1e-6));
    if (accepted.length > 0 && maxVertexDist(positions, weldedIndices, t, plane) > distTol) continue;

    accepted.push(t);
    depths.push(d);
    if (d > maxDepth) maxDepth = d;
    accumulateTriangle(acc, positions, weldedIndices, t, areas[t]);

    for (let k = 0; k < 3; k++) {
      const v = vertexAt(positions, weldedIndices[t * 3 + k]);
      if (v[0] < minX) minX = v[0];
      if (v[1] < minY) minY = v[1];
      if (v[2] < minZ) minZ = v[2];
      if (v[0] > maxX) maxX = v[0];
      if (v[1] > maxY) maxY = v[1];
      if (v[2] > maxZ) maxZ = v[2];
    }
    regionDiag = Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2 + (maxZ - minZ) ** 2);

    if (accepted.length >= maxTriangles) {
      truncated = true;
      break;
    }

    if (accepted.length >= nextRefit) {
      const refit = fitFromAccumulator(acc);
      if (dot(refit.normal, plane.normal) < 0) refit.normal = scale(refit.normal, -1);
      plane = refit;
      nextRefit *= 2;
    }

    for (let s = 0; s < 3; s++) {
      const nb = adj.adjacent[t * 3 + s];
      if (nb >= 0 && !visited[nb]) {
        visited[nb] = 1;
        queue.push(nb);
        queueDepth.push(d + 1);
      }
    }
  }

  return {
    triangles: new Uint32Array(accepted),
    depths: new Uint32Array(depths),
    plane,
    seedTriangle,
    maxDepth,
    truncated,
  };
}
